import { PromptInput } from 'components/PromptInput/PromptInput'
import Styles from './RevisionPromptInput.module.scss'
import { AI } from 'blaze-ui/icons'
import { Selection, useCreateRevision } from 'hooks/queries/layers/revisions/useCreateRevision'
import { AllSelection, NodeSelection, TextSelection } from 'prosemirror-state'
import { observer } from 'mobx-react-lite'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { useDocStore } from 'stores/DocStore'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { RevisionType } from 'components/BlazeCommandLine/Types'
import { useAlmanacEditorState } from 'stores/EditorState'
import { useState } from 'react'
import { useFocusWithin } from '@react-aria/interactions'
import cx from 'classnames'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Send2 } from 'blaze-ui/icons/16'
import { useRootStore } from 'stores/RootStore'
import { AIRequestDrawer } from '../MobileToolbar/AIRequestDrawer/AIRequestDrawer'
import { useCreateAISuggestionNode } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'
import { isEmptyDoc } from 'common/Prose/utils/isEmptyDoc'
import { injectContent } from 'common/Prose/utils/injectContent'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

interface Props {
  articleId: string
  layerId: string
}

const RevisionPromptInput = observer(({ articleId, layerId }: Props) => {
  const view = useEditorView()
  const { drawersStore } = useRootStore()
  const editorState = useAlmanacEditorState()
  const isDocEmpty = editorState?.view.state && isEmptyDoc(editorState.view.state)
  const [customPrompt, setCustomPrompt] = useState('')
  const [isRevisionPromptFocused, setIsRevisionPromptFocused] = useState(false)

  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => setIsRevisionPromptFocused(isFocusWithin),
  })

  const generateMutation = useCreateAISuggestionNode({ articleId, layerId })

  const { mutate, isLoading } = useCreateRevision(
    { articleKey: articleId, layerId },
    {
      onSuccess: () => {
        const tr = view.state.tr
        tr.setSelection(TextSelection.create(view.state.doc, 0))
        view.dispatch(tr)

        drawersStore.openDrawer(AIRequestDrawer)
      },
    },
  )

  const handleSubmit = () => {
    setCustomPrompt('')

    if (isDocEmpty) {
      const selection = new NodeSelection(view.state.doc.resolve(0))
      const newDoc = injectContent(view.state, selection, '<<AI_CONTENT>>')
      const documentAsMarkdown = markdownSerializer.serialize(newDoc)

      generateMutation.mutate(
        {
          selection,
          documentAsMarkdown,
          prompt: {
            generation_type: GenerationType.InlineGeneric,
            topic: customPrompt,
          },
        },
        {
          onError: () => {
            showToastMessage('An error occurred when generating your content', 'error')
          },
        },
      )
      return
    }

    let proseSelection = editorState?.selection || new AllSelection(view.state.doc)
    if (proseSelection.empty) proseSelection = new AllSelection(view.state.doc)
    const doc = view.state.doc
    const documentAsMarkdown = markdownSerializer.serialize(doc)
    const selectionContent = doc.cut(proseSelection.from, proseSelection.to)
    const selectionAsMarkdown = markdownSerializer.serialize(selectionContent)

    mutate(
      {
        prompt: { custom_prompt: customPrompt, revision_type: RevisionType.CUSTOM_PROMPT },
        selection: proseSelection.toJSON() as Selection,
        selectionAsMarkdown,
        documentAsMarkdown,
      },
      {
        onSettled: () => {
          setCustomPrompt('')
          setIsRevisionPromptFocused(false)
        },
      },
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation()

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (customPrompt.trim().length > 2) handleSubmit()
    }
  }

  return (
    <div className={cx(Styles.container, { [Styles.focused]: isRevisionPromptFocused })} {...focusWithinProps}>
      <div className={Styles.inputContainer}>
        <AI size={24} />
        <PromptInput
          classNameTextarea={cx(Styles.textarea, { [Styles.withInput]: customPrompt.length > 0 })}
          placeholder="Ask Blaze to edit"
          onKeyDown={handleKeyDown}
          prompt={customPrompt}
          onChange={setCustomPrompt}
          onSubmit={handleSubmit}
          className={Styles.input}
          withIcon={false}
          isDisabled={isLoading}
          onFocusChange={(focused) => {
            if (focused) {
              view.dispatch(view.state.tr.scrollIntoView())
            } else {
              setTimeout(() => setCustomPrompt(''), 500)
            }
          }}
        />
      </div>
      {isRevisionPromptFocused && (
        <IconButton
          size="lg"
          icon={Send2}
          onPress={handleSubmit}
          aria-label="Ask Blaze"
          isDisabled={customPrompt.trim().length < 3}
        />
      )}
    </div>
  )
})

const RevisionPromptInputContainer = observer(() => {
  const docStore = useDocStore()

  if (!docStore.layerId) {
    return null
  }

  return <RevisionPromptInput articleId={docStore.articleId} layerId={docStore.layerId} />
})

export { RevisionPromptInputContainer as RevisionPromptInput }
